import api from './api.js';

const CART_STORAGE_KEY = 'inza_cart';

class CartService {
  // Récupérer le panier depuis l'API (si connecté) ou localStorage
  async getCart() {
    try {
      // Tenter de récupérer depuis l'API d'abord
      const response = await api.get('/cart/');
      // La nouvelle structure response.data = { cart_items: [...], user: {...} }
      return response.data.cart_items || [];
    } catch (error) {
      console.log('Cart not found in API, using localStorage');
      // Fallback vers localStorage si l'API échoue
      return this.getLocalCart();
    }
  }

  // Récupérer les informations utilisateur et le panier
  async getCartWithUser() {
    try {
      const response = await api.get('/cart/');
      return {
        cartItems: response.data.cart_items || [],
        user: response.data.user || null
      };
    } catch (error) {
      console.log('Cart not found in API, using localStorage');
      return {
        cartItems: this.getLocalCart(),
        user: null
      };
    }
  }

  // Récupérer le panier depuis le localStorage (fallback)
  getLocalCart() {
    if (typeof window === 'undefined') return [];
    
    try {
      const cart = localStorage.getItem(CART_STORAGE_KEY);
      return cart ? JSON.parse(cart) : [];
    } catch (error) {
      console.error('Error getting cart from localStorage:', error);
      return [];
    }
  }

  // Sauvegarder le panier dans le localStorage
  saveLocalCart(cartItems) {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }

  // Ajouter un article au panier (avec sauvegarde BDD)
  async addToCart(product, configuration, offer = null) {
    try {
      // Préparer les données pour l'API
      const cartData = {
        product: product.id,
        quantity: 1,
        storage: configuration.storage,
        color: configuration.color,
        offer: offer?.id || null
      };

      // Envoyer à l'API
      const response = await api.post('/cart/', cartData);
      
      // Mettre à jour le localStorage en fallback
      const localCart = this.getLocalCart();
      const existingItemIndex = localCart.findIndex(
        item => item.id === product.id && 
               item.storage === configuration.storage && 
               item.color === configuration.color
      );

      if (existingItemIndex >= 0) {
        localCart[existingItemIndex].quantity += 1;
      } else {
        localCart.push({
          ...product,
          storage: configuration.storage,
          color: configuration.color,
          quantity: 1,
          offer: offer,
          addedAt: new Date().toISOString()
        });
      }

      this.saveLocalCart(localCart);
      
      // Retourner le panier mis à jour depuis l'API
      return await this.getCart();
      
    } catch (error) {
      console.error('Error adding to cart via API, using localStorage:', error);
      
      // Vérifier si c'est une erreur 405 ou 404
      if (error.response && (error.response.status === 405 || error.response.status === 404)) {
        console.log('API endpoint not available, using localStorage only');
      }
      
      // Fallback vers localStorage si l'API échoue
      const cart = this.getLocalCart();
      const existingItemIndex = cart.findIndex(
        item => item.id === product.id && 
               item.storage === configuration.storage && 
               item.color === configuration.color
      );

      if (existingItemIndex >= 0) {
        cart[existingItemIndex].quantity += 1;
      } else {
        cart.push({
          ...product,
          storage: configuration.storage,
          color: configuration.color,
          quantity: 1,
          offer: offer,
          addedAt: new Date().toISOString()
        });
      }

      this.saveLocalCart(cart);
      return [...cart];
    }
  }

  // Mettre à jour la quantité d'un article
  async updateQuantity(productId, storage, color, newQuantity) {
    if (newQuantity < 1) return await this.removeFromCart(productId, storage, color);

    try {
      // Mettre à jour via l'API
      await api.put(`/cart/${productId}/`, {
        quantity: newQuantity,
        storage,
        color
      });
      
      // Mettre à jour le localStorage
      const cart = this.getLocalCart();
      const itemIndex = cart.findIndex(
        item => item.id === productId && 
               item.storage === storage && 
               item.color === color
      );

      if (itemIndex >= 0) {
        cart[itemIndex].quantity = newQuantity;
        this.saveLocalCart(cart);
      }

      return await this.getCart();
    } catch (error) {
      console.error('Error updating quantity via API, using localStorage:', error);
      
      // Fallback localStorage
      const cart = this.getLocalCart();
      const itemIndex = cart.findIndex(
        item => item.id === productId && 
               item.storage === storage && 
               item.color === color
      );

      if (itemIndex >= 0) {
        cart[itemIndex].quantity = newQuantity;
        this.saveLocalCart(cart);
      }

      return [...cart];
    }
  }

  // Supprimer un article du panier
  async removeFromCart(productId, storage, color) {
    try {
      // Essayer de supprimer via l'API mais ne pas bloquer si ça échoue
      await api.delete(`/cart/${productId}/`, {
        data: { storage, color }
      });
    } catch (error) {
      // Ignorer silencieusement les erreurs API et utiliser localStorage
      if (error.response && error.response.status === 405) {
        console.log('DELETE method not supported, using localStorage only');
      } else {
        console.error('Error removing from cart via API, using localStorage:', error);
      }
    }
    
    // Mettre à jour le localStorage (fonctionne toujours)
    const cart = this.getLocalCart();
    const updatedCart = cart.filter(
      item => !(item.id === productId && 
               item.storage === storage && 
               item.color === color)
    );
    
    this.saveLocalCart(updatedCart);
    return updatedCart;
  }

  // Vider le panier
  async clearCart() {
    // Vider uniquement le localStorage pour éviter les erreurs API
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
    return [];
  }

  // Calculer le total du panier
  async calculateTotal(cartItems = null) {
    const items = cartItems || await this.getCart();
    return items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }

  // Compter le nombre total d'articles dans le panier
  async getItemCount() {
    const cart = await this.getCart();
    return cart.reduce((count, item) => count + item.quantity, 0);
  }
}

const cartService = new CartService();
export default cartService;
